import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { Button } from 'primereact/button'

import { isGuest as isPageGuest, isSurveyStarted } from '@aytm/app/stats/lib/stat-page'
import useDataRun from '@aytm/app/stats/page/question-views/components/data-run/states/useDataRun'

import {
  calcRunLabel,
  countCurrentTime,
  countTotalTime,
  isRunOffered,
  JOB_STATUS
} from '@aytm/app/stats/page/question-views/components/data-run/lib/utils'
import { resolveCause } from '@aytm/app/stats/page/question-views/components/data-run/lib/causes'
import QuestionState from '@aytm/app/stats/page/question-views/components/data-run/QuestionState'

import style from '@aytm/app/stats/page/question-views/components/data-run/styles/DataRun.module.scss'

// The run panel, ported from the coffee DataRunView: it offers to crunch the numbers for a
// question whose data is incomplete, and reports the job's progress while it runs. The crunching
// itself is page work and stays behind `onRun` — this owns the offer, the label and the bar.
//
// What it says in place of a chart is resolved from the cause, not passed in per block: two of the
// five types never passed anything, which is how the slot came to render a title over an empty line.
const DataRun = ({
  answer,
  badData = false,
  type,
  jobUrl,
  jobType,
  defaultJobStatus,
  questionKey,
  schemaKey,
  icon = null,
  setUp = true,
  missing,
  colorTheme,
  showData = true,
  calcBadData,
  onBadData,
  onComplete
}) => {
  const { jobStatus, jobProgress, jobEta, isRunning, onRun } = useDataRun({
    answer,
    type,
    questionKey,
    schemaKey,
    jobUrl,
    jobType,
    defaultJobStatus,
    calcBadData,
    onBadData,
    onComplete
  })

  const isGuest = isPageGuest()

  if (!isRunOffered({ badData, answer })) return null

  // a message is left behind only by an analysis that ran and failed, which is what separates
  // that from one that was never run
  const failed = Boolean(answer?.badDataMessage?.length)
  // a block runs only through a job of its own -- a job type, or the run-report host's own process
  // URL -- and the rest never offered a Run button
  const runnable = Boolean(jobType || jobUrl)
  const cause = resolveCause({ type, jobStatus, isRunning, setUp, failed, isGuest, runnable })
  const showWarning = !badData && (answer?.difference > 0 || false === answer?.actualData)
  const substantial = answer?.difference > 5

  // A report the whole data set agrees with can still rest on too few respondents to trust. That
  // is a different caution from the one above and never shown beside it.
  const showSampleWarning = !badData && !showWarning && true === answer?.insufficientSample

  const progressWidth = () => {
    const total = countTotalTime({ jobProgress, jobEta })

    return total ? `${Math.min(100, (100 * countCurrentTime({ jobProgress, jobEta })) / total)}%` : '0%'
  }

  return (
    <>
      <div className={style.head}>
        {icon && badData ? <div className={style.icon}>{icon}</div> : null}

        {JOB_STATUS.disabled !== jobStatus ? (
          <>
            {showWarning ? (
              <div className={style.warning}>
                <i className="icon-aytmfd-help-v2" />
                <span>
                  <b>Attention:</b>&nbsp;report below {substantial ? 'substantially ' : ''}differs from the whole data
                  set.{' '}
                  {substantial || isRunning || isGuest
                    ? `Wait until new report is done or survey is closed${isGuest ? '' : ' before analyzing'}.`
                    : 'Rerun the report or wait until survey is closed before analyzing.'}
                </span>
              </div>
            ) : null}

            {showSampleWarning ? (
              <div className={style.warning}>
                <i className="icon-aytmfd-help-v2" />
                <span>
                  <b>Attention:</b>&nbsp;the sample size is insufficient for a reliable analysis.
                  {isSurveyStarted() ? ' Please wait until survey is fulfilled before analyzing.' : ''}
                </span>
              </div>
            ) : null}

            {(badData && showData) || !runnable || isGuest ? null : (
              <div className={style.buttonBox}>
                <Button
                  data-testid="stat-data-run-button"
                  className="p-button-rounded p-button-blue"
                  label={calcRunLabel({ badData, answer, isRunning, jobEta })}
                  disabled={isRunning}
                  onClick={onRun}
                >
                  {isRunning ? <span className={style.progress} style={{ width: progressWidth() }} /> : null}
                </Button>
              </div>
            )}
          </>
        ) : null}
      </div>

      {badData && showData ? (
        <QuestionState
          type={type}
          cause={cause}
          missing={missing}
          colorTheme={colorTheme}
          jobProgress={jobProgress}
          canRun={runnable && !isGuest}
          onRun={onRun}
        />
      ) : null}
    </>
  )
}

DataRun.displayName = 'DataRun'

DataRun.propTypes = {
  answer: answerPropType,
  badData: PropTypes.bool,
  type: PropTypes.string,
  jobUrl: PropTypes.string,
  jobType: PropTypes.string,
  // the status a question with none of its own starts at; a block that is only the run band says
  // `idle`, since there is nothing for a disabled band to sit above
  defaultJobStatus: PropTypes.string,
  questionKey: PropTypes.string,
  schemaKey: PropTypes.string,
  icon: PropTypes.node,
  // a question missing something the analysis needs, supplied in the editor rather than by respondents
  setUp: PropTypes.bool,
  missing: PropTypes.string,
  colorTheme: PropTypes.array,
  showData: PropTypes.bool,
  calcBadData: PropTypes.func,
  onBadData: PropTypes.func,
  onComplete: PropTypes.func
}

export default DataRun
